/**
 * Beheerdersactie: target vorig jaar en RF02, beide uit kolom K "Nieuw Target" (Joris 28 sep)
 * eenmalig uit het Revenue-blad.
 * Stap 1 voorstel (niets opgeslagen), stap 2 importeren; al ingevulde targets alleen na
 * bevestiging overschrijven. Ongedaan maken zet per account de vorige waarde terug.
 */
import React, { useState } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { budgetApi } from './budgetApi';
import { euro } from './budgetLogica';

const KLEIN = { fontSize: 13, color: '#64748b' };

export default function ImportDialoog({ open, jaar, onClose, onGeimporteerd }) {
  const [bestand, setBestand] = useState(null);
  const [voorstel, setVoorstel] = useState(null);
  const [metGelijkend, setMetGelijkend] = useState(true);
  const [overschrijf, setOverschrijf] = useState(false);
  const [bezig, setBezig] = useState(false);
  const vorig = jaar - 1;

  const sluit = () => { setBestand(null); setVoorstel(null); setOverschrijf(false); onClose(); };

  const bekijk = async (f, gelijkend = metGelijkend) => {
    if (!f) return;
    setBezig(true);
    try {
      const uit = await budgetApi.importTargets(f, { jaar, toepassen: false, metGelijkend: gelijkend });
      setVoorstel(uit.voorstel);
    } catch (e) { toast.error(e.message); setVoorstel(null); } finally { setBezig(false); }
  };

  const importeer = async () => {
    const aantalOver = voorstel.accounts.filter((a) => a.al_ingevuld).length;
    if (overschrijf && aantalOver > 0) {
      const ok = await bevestig({
        titel: 'Bestaande targets overschrijven?',
        tekst: `${aantalOver} ${aantalOver === 1 ? 'account heeft' : 'accounts hebben'} al een target ${vorig}. Die worden vervangen door de waarde uit de Excel.`,
        knop: 'Overschrijven',
      });
      if (!ok) return;
    }
    setBezig(true);
    try {
      const uit = await budgetApi.importTargets(bestand, { jaar, toepassen: true, overschrijf, metGelijkend, voorstelId: voorstel.voorstel_id });
      onGeimporteerd();
      sluit();
      const n = uit.bijgewerkt.length;
      if (!n) { toast(uit.overgeslagen.length ? 'Niets bijgewerkt: alle targets waren al ingevuld' : 'Niets te importeren'); return; }
      // Ongedaan maken in één keer op de server (alles of niets, met versiecontrole).
      biedOngedaanAan(`Target ${vorig} ingelezen voor ${n} ${n === 1 ? 'account' : 'accounts'}${uit.overgeslagen.length ? ` (${uit.overgeslagen.length} al ingevuld, overgeslagen)` : ''}`, async () => {
        try {
          await budgetApi.herstelImport(uit.bijgewerkt.map(({ sleutel, vorige, vorige_rf02: vorigeRf02, versie }) => ({ sleutel, vorige, vorige_rf02: vorigeRf02, versie })), jaar);
        } finally { onGeimporteerd(); }
      });
    } catch (e) {
      toast.error(e.message);
      if (e.status === 409 && e.voorstel) setVoorstel(e.voorstel);   // toon het actuele voorstel
    } finally { setBezig(false); }
  };

  const ongekoppeld = voorstel?.regels.filter((r) => r.match === 'geen') || [];
  const gelijkend = voorstel?.regels.filter((r) => r.match === 'gelijkend') || [];
  const alIngevuld = voorstel?.accounts.filter((a) => a.al_ingevuld) || [];

  return (
    <Dialog open={open} onClose={sluit} maxWidth="sm" fullWidth>
      <DialogTitle>Targets {vorig} importeren uit Excel</DialogTitle>
      <DialogContent>
        <Typography sx={{ ...KLEIN, mb: 2 }}>
          Kies de reforecast-Excel. We lezen blad Revenue, kolom K (Nieuw Target, ook als RF02) per bedrijf, koppelen op de
          Gripp-naam en tellen op per account. Het bestand wordt niet bewaard.
        </Typography>
        <Button component="label" variant="outlined" size="small" disabled={bezig} sx={{ textTransform: 'none' }}>
          {bestand ? bestand.name : 'Kies .xlsx'}
          <input hidden type="file" accept=".xlsx" data-testid="budget-import-bestand"
            onChange={(e) => { const f = e.target.files?.[0] || null; setBestand(f); setVoorstel(null); bekijk(f); }} />
        </Button>

        {voorstel && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {voorstel.accounts.length} accounts · samen {euro(voorstel.totaal)}
            </Typography>
            <Typography sx={KLEIN}>
              {voorstel.aantal.exact} regels gekoppeld · {voorstel.aantal.gelijkend} bij benadering · {voorstel.aantal.geen} niet gekoppeld
            </Typography>

            {gelijkend.length > 0 && (
              <Box component="details" sx={{ mt: 1.5, fontSize: 13 }}>
                <summary style={{ cursor: 'pointer' }}>Bij benadering gekoppeld ({gelijkend.length})</summary>
                {gelijkend.map((r) => <Typography key={r.rij} sx={KLEIN}>{r.naam} → {r.account} ({euro(r.target)})</Typography>)}
              </Box>
            )}
            {gelijkend.length > 0 && (
              <FormControlLabel sx={{ mt: 0.5 }} control={<Checkbox size="small" checked={metGelijkend}
                onChange={(e) => { setMetGelijkend(e.target.checked); bekijk(bestand, e.target.checked); }} />}
                label={<Typography sx={{ fontSize: 13.5 }}>Koppelingen bij benadering meenemen</Typography>} />
            )}
            {ongekoppeld.length > 0 && (
              <Box component="details" sx={{ mt: 1, fontSize: 13 }}>
                <summary style={{ cursor: 'pointer' }}>Niet gekoppeld ({ongekoppeld.length}) — vul die met de hand in</summary>
                {ongekoppeld.map((r) => <Typography key={r.rij} sx={KLEIN}>Rij {r.rij}: {r.naam}{r.target != null ? ` (${euro(r.target)})` : ''} — {r.reden}</Typography>)}
              </Box>
            )}
            {alIngevuld.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <FormControlLabel control={<Checkbox size="small" checked={overschrijf} onChange={(e) => setOverschrijf(e.target.checked)} />}
                  label={<Typography sx={{ fontSize: 13.5 }}>Ook de {alIngevuld.length} al ingevulde targets overschrijven</Typography>} />
                <Box component="details" sx={{ fontSize: 13 }}>
                  <summary style={{ cursor: 'pointer' }}>Welke</summary>
                  {alIngevuld.map((a) => <Typography key={a.sleutel} sx={KLEIN}>{a.naam}: nu {euro(a.huidig)}, Excel {euro(a.target)}</Typography>)}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={sluit} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation disabled={bezig || !voorstel || !voorstel.accounts.length}
          onClick={importeer} sx={{ textTransform: 'none', fontWeight: 700 }}>Importeren</Button>
      </DialogActions>
    </Dialog>
  );
}
